import {
  Badge,
  Box,
  Chip,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import TableChartIcon from '@mui/icons-material/TableChart'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import FilterListIcon from '@mui/icons-material/FilterList'
import ClearIcon from '@mui/icons-material/Clear'
import {
  ProductionRequirementRowType,
  SchedulerCleningMappingRowType,
  SchedulerPackagingRoutesRowType,
  SchedulerTabsProps,
  SynchronizedLinesRowType,
  WorkstationDownTimeRowType,
} from './Scheduler.types'
import {
  useDeleteProductionRequirements,
  useRunSchedulerDirect,
  useScheduleProductionRequirements,
  useConsolidatedWeeks,
  useUpdateProductionRequirements,
  useUploadScheduleDataFile,
} from './Scheduler.data'
import { useQueryClient } from 'react-query'
import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import toast from 'react-hot-toast'
import { RunFailure, toastRunFailure } from './RunFailureDetails'
import { SchedulerPackagingRoutesTable } from './SchedulerPackagingRoutes.muiTable'
import { cellText, restoreTankCells, withoutTanks } from './tankRoutes'
import { FactoryLayout, FactoryLayoutFilter } from './FactoryLayout.view'
import { FactoryBottomDetail } from './FactoryBottomDetail.view'
import { FactoryScheduleView, TimelineTab } from './FactoryScheduleView.view'
import { MDRunFocus } from './ModelDescription.view'
import { ScheduleFullGanttView } from './ScheduleFullGantt.view'
import { ScheduleChangeoverView } from './ScheduleChangeoverView.view'
import { ScheduleConstraintCardsView } from './ScheduleConstraintCards.view'
import { ScheduleCrewSyncView } from './ScheduleCrewSync.view'
import { ScheduleControlPanel } from './ScheduleControlPanel.view'
import {
  useSchedulerData,
  SchedulerDataVariant,
} from 'Features/SchedulerView/SchedulerView.data'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import { weekBaseHourFor } from 'Features/SchedulerView/ClipToWeek'

import { DowntimeGantt } from './DowntimeGantt.view'
import { ResourceGantt } from './ScheduleGantt.view'
import {
  ProductEditPanel,
  KitchenProductEditPanel,
} from './ScheduleProductEdit.view'

// ─── Lightweight read-only detail table for the bottom panel ─────────────────
// Memoised so it only re-renders when rows/columns actually change (not on every parent render).
// Using a fixed height instead of autoHeight avoids expensive layout recalculations.
const DetailTable = memo(
  ({
    rows,
    columns,
  }: {
    rows: Record<string, unknown>[]
    columns: GridColDef[]
  }) => (
    <Box sx={{ height: Math.min(400, 36 + rows.length * 32), minHeight: 120 }}>
      <DataGridPremium
        rows={rows}
        columns={columns}
        density="compact"
        hideFooter={rows.length <= 25}
        pageSizeOptions={[25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        disableColumnMenu
        disableRowSelectionOnClick
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': { bgcolor: 'grey.50' },
          '& .MuiDataGrid-cell': { fontSize: '0.8rem' },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '0.8rem',
            fontWeight: 600,
          },
        }}
      />
    </Box>
  )
)

// ─── Helper: create table handlers for a specific API endpoint ──────────────
const useTableHandlers = <T,>(
  schedulerId: string,
  api: string,
  iteration: string,
  week?: number
) => {
  const onUpdateSuccess = () => toast.success('Data updated')
  const onUpdateFailure = () => toast.error('Failed to update data')
  const {
    mutate: saveData,
    mutateAsync: saveDataAsync,
    isLoading: isSaving,
  } = useUpdateProductionRequirements(onUpdateSuccess, onUpdateFailure)

  const onDeleteSuccess = () => toast.success('Rows deleted')
  const onDeleteFailure = () => toast.error('Failed to delete rows')
  const { mutateAsync: deleteData, isLoading: isDeleting } =
    useDeleteProductionRequirements(onDeleteSuccess, onDeleteFailure)

  const [updateRowsStatus, setUpdateRowsStatus] = useState({})
  const onUploadSuccess = (mergeInfo: Record<string, unknown>) => {
    setUpdateRowsStatus(mergeInfo)
    toast.success('Data uploaded')
  }
  const onUploadFailure = () => toast.error('Failed to upload data')
  const { mutate: uploadData, isLoading: isUploading } =
    useUploadScheduleDataFile(onUploadSuccess, onUploadFailure)

  const updateTableData = useCallback(
    (changedRows: Record<number, T>) => {
      saveData({
        data: changedRows as Record<number, SchedulerPackagingRoutesRowType>,
        schedulerId,
        API: api,
        iteration,
        week,
      })
    },
    [saveData, schedulerId, api, iteration, week]
  )

  // Awaitable variant — needed where a caller must wait for the PUT to land
  // (e.g. mid-week reschedule: the downtime save must actually commit before
  // the run-scheduler request reads that same table) rather than firing both
  // as unordered, unrelated mutations.
  const updateTableDataAsync = useCallback(
    (changedRows: Record<number, T>) =>
      saveDataAsync({
        data: changedRows as Record<number, SchedulerPackagingRoutesRowType>,
        schedulerId,
        API: api,
        iteration,
        week,
      }),
    [saveDataAsync, schedulerId, api, iteration, week]
  )

  const uploadScheduleData = useCallback(
    (file: File) => {
      uploadData({ file, schedulerId, API: api, iteration, weekNumber: week })
    },
    [uploadData, schedulerId, api, iteration, week]
  )

  const deleteTableData = useCallback(
    (rows: T[]) =>
      deleteData({
        rows: rows as unknown as Record<string, unknown>[],
        schedulerId,
        API: api,
        iteration,
        // See the twin of this hook in Scheduler.data.ts: a per-week table
        // deleted without a week targets the base folder and silently
        // no-ops.
        week,
      }),
    [deleteData, schedulerId, api, iteration, week]
  )

  return {
    updateTableData,
    updateTableDataAsync,
    uploadScheduleData,
    deleteTableData,
    isSaving,
    isUploading,
    isDeleting,
    updateRowsStatus,
  }
}

// ─── Resizable Factory Schedule Panel ────────────────────────────────────────

/** Vertically resizable split: FactoryLayout on top, Factory Schedule on bottom. */
const ResizableFactoryView = ({
  schedulerId,
  iteration,
  data,
  downtimeData,
  cleaningMapping,
  productionRequirements,
  synchronizedLines,
  outputData,
  consolidatedWeeks,
  dataSummary,
  onFilterChange,
  onSaveData,
  onUploadFile,
  onDeleteRows,
  selectedWeek,
  onWeekChange,
  showSchedule,
  bottomContent,
  onBatchSelect,
  pendingComment,
  onClearPendingComment,
  notProducedIds,
  onToggleNotProduced,
  onResetNotProduced,
}: {
  schedulerId: string
  iteration: string
  data: any
  downtimeData: any
  cleaningMapping: any
  productionRequirements: any
  synchronizedLines: any
  outputData: any
  consolidatedWeeks?: number[]
  dataSummary: any
  onFilterChange: any
  onSaveData: any
  onUploadFile: any
  onDeleteRows: any
  selectedWeek: number
  onWeekChange: (week: number) => void
  showSchedule: boolean
  bottomContent?: React.ReactNode
  onBatchSelect?: (
    resourceName: string,
    resourceType: 'kitchen' | 'line',
    productCode: string,
    startTime: number,
    endTime: number
  ) => void
  pendingComment?: {
    productCode: string
    resourceName: string
    resourceType: 'kitchen' | 'line'
    startTime: number
    endTime: number
  } | null
  onClearPendingComment?: () => void
  notProducedIds?: Set<string>
  onToggleNotProduced?: (barKey: string) => void
  onResetNotProduced?: () => void
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [topHeight, setTopHeight] = useState<number | null>(null)

  // Initialize to 60% of container height on first render
  useEffect(() => {
    if (topHeight === null && containerRef.current) {
      setTopHeight(containerRef.current.getBoundingClientRect().height * 0.6)
    }
  })

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()

    const onMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const relativeY = e.clientY - rect.top
      const newHeight = Math.max(200, Math.min(rect.height - 150, relativeY))
      setTopHeight(newHeight)
    }

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    document.body.style.cursor = 'row-resize'
    document.body.style.userSelect = 'none'
  }, [])

  const hasBottomContent = showSchedule || !!bottomContent

  if (!hasBottomContent) {
    return (
      <FactoryLayout
        schedulerId={schedulerId}
        iteration={iteration}
        packagingRoutes={data}
        downtimeData={downtimeData}
        cleaningMapping={cleaningMapping}
        productionRequirements={productionRequirements}
        synchronizedLines={synchronizedLines}
        outputData={outputData}
        consolidatedWeeks={consolidatedWeeks}
        dataSummary={dataSummary}
        onFilterChange={onFilterChange}
        onSaveData={onSaveData}
        onUploadFile={onUploadFile}
        onDeleteRows={onDeleteRows}
        selectedWeek={selectedWeek}
        onWeekChange={onWeekChange}
        pendingComment={pendingComment}
        onClearPendingComment={onClearPendingComment}
        notProducedIds={notProducedIds}
        onResetNotProduced={onResetNotProduced}
      />
    )
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 160px)',
        minHeight: 500,
      }}
    >
      {/* Top: Factory Layout */}
      <Box
        sx={{
          height: topHeight ?? '60%',
          flexShrink: 0,
          overflow: 'auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <FactoryLayout
          schedulerId={schedulerId}
          iteration={iteration}
          packagingRoutes={data}
          downtimeData={downtimeData}
          cleaningMapping={cleaningMapping}
          productionRequirements={productionRequirements}
          synchronizedLines={synchronizedLines}
          outputData={outputData}
          consolidatedWeeks={consolidatedWeeks}
          dataSummary={dataSummary}
          onFilterChange={onFilterChange}
          onSaveData={onSaveData}
          onUploadFile={onUploadFile}
          onDeleteRows={onDeleteRows}
          selectedWeek={selectedWeek}
          onWeekChange={onWeekChange}
          pendingComment={pendingComment}
          onClearPendingComment={onClearPendingComment}
          notProducedIds={notProducedIds}
          onResetNotProduced={onResetNotProduced}
        />
      </Box>

      {/* Drag handle */}
      <Box
        onMouseDown={onMouseDown}
        sx={{
          height: 10,
          flexShrink: 0,
          cursor: 'row-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          '&:hover': { bgcolor: 'grey.300' },
          transition: 'background-color 0.15s',
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 3,
            borderRadius: 1.5,
            bgcolor: 'grey.400',
          }}
        />
      </Box>

      {/* Bottom: Factory Schedule or Detail */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 100 }}>
        {bottomContent ?? (
          <ScheduleFullGanttView
            schedulerId={schedulerId}
            iteration={iteration}
            selectedWeek={selectedWeek}
            onWeekChange={onWeekChange}
            onBatchSelect={onBatchSelect}
            notProducedIds={notProducedIds}
            onToggleNotProduced={onToggleNotProduced}
          />
        )}
      </Box>
    </Box>
  )
}

export const SchedulePackagingRoutesView = ({
  schedulerId,
  iteration,
  forceViewMode,
  onDataEdited,
  selectedWeek: externalSelectedWeek,
  onGoToScheduleTab,
  outputVariant: externalOutputVariant,
  onOutputVariantChange: externalOnOutputVariantChange,
}: SchedulerTabsProps & {
  forceViewMode?: 'layout' | 'table'
  onDataEdited?: (edited: boolean) => void
  /** When provided, the page-level week selector drives this component. */
  selectedWeek?: number
  /** Called when a run completes, so the page-level sidebar can bring the
   * "Schedule" section into view (the outer navigation this component's own
   * `activeTab`/`viewMode` state can't reach). */
  onGoToScheduleTab?: () => void
  /** When provided, the page holds the original/rescheduled choice, so the
   *  top strip's answer picker can follow it — it must not offer the original
   *  run's answers while a reschedule is on screen. */
  outputVariant?: SchedulerDataVariant
  onOutputVariantChange?: (variant: SchedulerDataVariant) => void
}) => {
  const [viewMode, setViewMode] = useState<'table' | 'layout'>(
    forceViewMode || 'layout'
  )
  const [pendingComment, setPendingComment] = useState<{
    productCode: string
    resourceName: string
    resourceType: 'kitchen' | 'line'
    startTime: number
    endTime: number
  } | null>(null)
  const [notProducedIds, setNotProducedIds] = useState<Set<string>>(new Set())
  const onToggleNotProduced = useCallback((key: string) => {
    setNotProducedIds((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  // ─── Mid-week reschedule edits in progress ────────────────────────────────
  //
  // Held here rather than inside `MidWeekRescheduleTab` because that panel is
  // unmounted by three ordinary actions — switching the bottom-strip tab,
  // clicking a Factory-layout asset (which swaps the whole view for
  // `FactoryBottomDetail`), and leaving the Factory tab — and typed quantities
  // are far too expensive to retype to lose that way. `notProducedIds` above
  // already lived here for the same reason, and the two were inconsistent:
  // ticks survived a tab switch while the figures beside them did not.
  //
  // None of this is persisted anywhere until the planner presses Lock &
  // re-run, which writes the edited products to the week's production
  // requirements and then clears these maps.
  //
  // `stillToMake` is keyed by RUN (product + line + start hour), the two
  // timing maps by PRODUCT — which is the grain of the column each one
  // writes. Raw strings, not numbers, so a half-typed entry is never
  // rewritten under the cursor.
  const [midWeekStillToMake, setMidWeekStillToMake] = useState<
    Record<string, string>
  >({})
  const [midWeekDontStartBefore, setMidWeekDontStartBefore] = useState<
    Record<string, string>
  >({})
  const [midWeekFinishBefore, setMidWeekFinishBefore] = useState<
    Record<string, string>
  >({})

  // Dedicated mid-week reschedule run — kept separate from any other
  // useRunSchedulerDirect instance (same convention ScheduleControlPanel
  // already uses: each mid-week-replan surface owns its own mutation/SSE).
  const midWeekQueryClient = useQueryClient()
  const onMidWeekRunSuccess = useCallback(() => {
    toast.success('Mid-week reschedule completed')
    setNotProducedIds(new Set())
    midWeekQueryClient.invalidateQueries(['scheduler', schedulerId, iteration])
    midWeekQueryClient.invalidateQueries([
      'scheduler-settings',
      schedulerId,
      iteration,
    ])
    onGoToScheduleTab?.()
  }, [midWeekQueryClient, schedulerId, iteration, onGoToScheduleTab])
  const onMidWeekRunFailure = useCallback(
    (errorMessage?: string, failures?: RunFailure[]) => {
      toastRunFailure(
        'Why the reschedule failed',
        errorMessage || 'Mid-week reschedule failed',
        failures
      )
    },
    []
  )
  const {
    mutate: runMidWeekReschedule,
    isLoading: isMidWeekRunning,
    progress: midWeekProgress,
  } = useRunSchedulerDirect(onMidWeekRunSuccess, onMidWeekRunFailure, {
    // A reschedule cannot start while the schedule is already running.
    schedulerId,
  })
  const API = 'packing-routes'
  const { data: responseData = { data: [], predicate: undefined }, isLoading } =
    useScheduleProductionRequirements(schedulerId, API, iteration)
  const { data = [], predicate } = responseData

  // Whether this schedule has anything to do with holding tanks, which decides
  // whether the grid renders its tank columns (route, segment, maturation).
  //
  // The question is "does the plant DECLARE a vessel", not "does a route
  // mention one" — and the difference is the whole setup flow. Declaring a tank
  // and then wiring its routes is the order a planner works in, so inferring
  // from the route rows alone would hide the very columns they came to fill in
  // and leave no way to turn them back on.
  //
  // The second clause is a safety net rather than the rule: a route that still
  // references a tank after the tank rows were deleted is exactly the mistake
  // worth SEEING, so data that exists is never hidden.
  const { data: tanksResponse = { data: [], predicate: undefined } } =
    useScheduleProductionRequirements(schedulerId, 'tanks', iteration)
  const declaredTanks = tanksResponse?.data ?? []
  // A tank is named in the Kitchen / Line cells now, and only a DECLARED name
  // is read as one — so the grid needs the names to mark those cells.
  const tankNames = useMemo(
    () =>
      declaredTanks
        .map((t: { tankId?: string | null }) => cellText(t?.tankId))
        .filter((t: string | null): t is string => t != null),
    [declaredTanks]
  )
  // Rows still in the pre-v7.152.0 shape. Their legacy tank columns are shown,
  // read-only, only while some row holds a value: hidden, they would still
  // steer the run with nothing on screen to say so.
  const hasLegacyTankColumns = useMemo(
    () =>
      (data ?? []).some(
        (r: SchedulerPackagingRoutesRowType) =>
          cellText(r?.inputTankId) != null ||
          cellText(r?.outputTankId) != null ||
          cellText(r?.inputProductCode) != null
      ),
    [data]
  )
  const showTankColumns = useMemo(
    () =>
      declaredTanks.length > 0 ||
      hasLegacyTankColumns ||
      (data ?? []).some(
        (r: SchedulerPackagingRoutesRowType) => r?.maturationHours != null
      ),
    [declaredTanks, data, hasLegacyTankColumns]
  )

  const setIsDataEdited = useCallback(
    (v: boolean) => onDataEdited?.(v),
    [onDataEdited]
  )
  const [selectedWeek, setSelectedWeek] = useState(externalSelectedWeek ?? 1)

  // Sync when the page-level tile selector changes week
  useEffect(() => {
    if (externalSelectedWeek !== undefined)
      setSelectedWeek(externalSelectedWeek)
  }, [externalSelectedWeek])

  // Fetch additional data for factory layout — only when in layout mode
  const isLayout = viewMode === 'layout'
  const { data: downtimeResponse = { data: [] } } =
    useScheduleProductionRequirements(
      schedulerId,
      'workstation-downtime',
      iteration,
      selectedWeek,
      isLayout
    )
  const { data: cleaningResponse = { data: [] } } =
    useScheduleProductionRequirements(
      schedulerId,
      'cleaning-mapping',
      iteration,
      undefined,
      isLayout
    )
  const { data: productionReqResponse = { data: [], predicate: undefined } } =
    useScheduleProductionRequirements(
      schedulerId,
      'production-requirements',
      iteration,
      selectedWeek,
      isLayout
    )
  const { data: syncLinesResponse = { data: [] } } =
    useScheduleProductionRequirements(
      schedulerId,
      'synchronized-lines',
      iteration,
      undefined,
      isLayout
    )
  const { data: inputMappingResponse = { data: [] } } =
    useScheduleProductionRequirements(
      schedulerId,
      'resource-and-input-mapping',
      iteration,
      undefined,
      isLayout
    )
  const { data: outputMappingResponse = { data: [] } } =
    useScheduleProductionRequirements(
      schedulerId,
      'resource-and-output-mapping',
      iteration,
      undefined,
      isLayout
    )
  const { data: constraintsResponse = { data: [] } } =
    useScheduleProductionRequirements(
      schedulerId,
      'resource-and-material-constraints',
      iteration,
      undefined,
      isLayout
    )

  // Compute data summary for the Data & Versions panel
  const dataSummary = useMemo(
    () => [
      { name: 'Packaging Routes', count: data?.length || 0 },
      {
        name: 'Production Requirements',
        count: productionReqResponse?.data?.length || 0,
      },
      {
        name: 'Workstation Downtime',
        count: downtimeResponse?.data?.length || 0,
      },
      { name: 'Cleaning Mapping', count: cleaningResponse?.data?.length || 0 },
      {
        name: 'Synchronized Lines',
        count: syncLinesResponse?.data?.length || 0,
      },
      {
        name: 'Resource Constraints',
        count: constraintsResponse?.data?.length || 0,
      },
      { name: 'Input Mapping', count: inputMappingResponse?.data?.length || 0 },
      {
        name: 'Output Mapping',
        count: outputMappingResponse?.data?.length || 0,
      },
    ],
    [
      data,
      productionReqResponse,
      downtimeResponse,
      cleaningResponse,
      syncLinesResponse,
      constraintsResponse,
      inputMappingResponse,
      outputMappingResponse,
    ]
  )

  // Which schedule the Factory schedule / output tabs display.
  //
  // Defaults to 'latest' — the most recent mid-week reschedule if one exists,
  // otherwise the original (resolved server-side). Pinning this to 'original'
  // meant a planner who had just re-run mid-week was shown the schedule they
  // had replaced, with no control anywhere in the Portal to see the new one:
  // the reschedule writes to Versions/{iter}/Rescheduled/{id}/ and nothing
  // ever asked for it. Reported 2026-09-15 as "no new production is appearing"
  // — the run was fine, the viewer could not show it.
  //
  // NOT the mid-week checklist's baseline, which stays on 'original' by
  // design — see `midWeekOutputData` below.
  const [localOutputVariant, setLocalOutputVariant] =
    useState<SchedulerDataVariant>('latest')
  const outputVariant = externalOutputVariant ?? localOutputVariant
  const setOutputVariant =
    externalOnOutputVariantChange ?? setLocalOutputVariant

  // Fetch schedule output data (if a schedule has been run)
  // No scheduleVariant argument: this follows the planner's selection via
  // ScheduleVariantContext, like every other results view.
  const { data: outputData } = useSchedulerData(
    schedulerId,
    iteration,
    selectedWeek,
    outputVariant
  )
  const outputRows: InputRow[] = useMemo(() => {
    const rows = (outputData?.data || []) as InputRow[]
    // If data has weekNumber, only keep rows matching selectedWeek
    const filtered = rows.filter(
      (r) => r.weekNumber == null || Number(r.weekNumber) === selectedWeek
    )
    return filtered
  }, [outputData, selectedWeek])

  // Whether this iteration HAS a mid-week reschedule, as reported by the
  // service — a 'latest' request that found no reschedule returns the
  // original, and the rows cannot be told apart.
  //
  // Asked of the 'latest' query whatever is on screen, not of `outputData`.
  // Reading it off the rows being shown made the toggle vanish the moment
  // the planner switched to the original (that response says
  // `rescheduled: false`), with no way back. While 'latest' is selected this
  // is the same query as `outputData`, so it costs no extra request; on
  // 'original' it is normally still in the cache.
  const { data: latestOutputData } = useSchedulerData(
    schedulerId,
    iteration,
    selectedWeek,
    'latest'
  )
  const hasReschedule = Boolean(
    (latestOutputData as { rescheduled?: boolean } | undefined)?.rescheduled
  )

  // The mid-week reschedule tab always builds its checklist from the
  // ORIGINAL schedule, never a prior reschedule — every mid-week reschedule
  // is computed independently against the original baseline (deliberate
  // product decision), matching what scheduler_api's previous_outputs read
  // now does server-side (see read.rs). Not-produced keys embed each row's
  // start time; those times shift on every reschedule, so building the
  // checklist from anything other than the original would silently break
  // key matching for a second reschedule.
  const { data: midWeekOutputData } = useSchedulerData(
    schedulerId,
    iteration,
    selectedWeek,
    'original',
    // Explicitly the default answer, NOT the planner's selection: this
    // checklist is built against the original baseline by design (see the
    // comment above), and an alternative answer carries no such baseline.
    'balanced'
  )
  const midWeekOutputRows: InputRow[] = useMemo(() => {
    const rows = (midWeekOutputData?.data || []) as InputRow[]
    return rows.filter(
      (r) => r.weekNumber == null || Number(r.weekNumber) === selectedWeek
    )
  }, [midWeekOutputData, selectedWeek])

  // Pull consolidated_weeks from the scheduler settings snapshot so the
  // FactoryLayout knows whether the rows it's displaying came from a
  // multi-week run. The snapshot is the authoritative record of how the
  // current schedule was produced (the in-session multi-week button state
  // can describe a future run, not the one already on disk).
  const consolidatedWeeks = useConsolidatedWeeks(schedulerId, iteration)

  // Absolute-horizon offset for the currently displayed week. Output rows'
  // kitchen_start_time/line_start_time are absolute across the whole
  // consolidated run (week 2 sits at 168-336, not 0-168), so the views have
  // to rebase before rendering and the mid-week reschedule tab has to add
  // this back to turn its day+time cutoff into the right absolute hour.
  // `null` means a single-week schedule whose rows are already 0-168.
  // Workstation downtime does NOT need this — it is stored per-week-relative
  // and rebased server-side at merge time.
  const midWeekBaseHour = weekBaseHourFor(consolidatedWeeks, selectedWeek)

  // A production bar selected on the timeline, which the Model description
  // below filters by. Lives here because the timeline and the output tabs are
  // siblings. Cleared when the week or the rows change: a selection names a
  // bar at particular hours, which a different week or a re-run no longer has.
  const [selectedRun, setSelectedRun] = useState<MDRunFocus | null>(null)
  const [revealModelTick, setRevealModelTick] = useState(0)
  useEffect(() => {
    setSelectedRun(null)
  }, [selectedWeek, outputRows])
  const explainRun = useCallback((f: MDRunFocus) => {
    setSelectedRun(f)
    setRevealModelTick((t) => t + 1)
  }, [])

  // ─── Primary packaging routes handlers ──────────────────────────────────────
  const onUpdateSuccess = () => {
    setIsDataEdited(true)
    toast.success('Production requirements updated successfully')
  }
  const onUpdateFailure = () => {
    toast.error('Failed to update production requirements')
  }
  const { mutate: saveData, isLoading: isUpdating } =
    useUpdateProductionRequirements(onUpdateSuccess, onUpdateFailure)

  const onDeleteSuccess = () => {
    toast.success('Rows deleted successfully')
  }
  const onDeleteFailure = () => {
    toast.error('Failed to delete rows')
  }
  const { mutateAsync: deleteData, isLoading: isDeleting } =
    useDeleteProductionRequirements(onDeleteSuccess, onDeleteFailure)

  const deleteTableData = (rows: SchedulerPackagingRoutesRowType[]) =>
    deleteData({ rows, schedulerId, API, iteration })

  const [allChangedRows, setAllChangedRows] = useState<
    Record<number, SchedulerPackagingRoutesRowType>
  >({})

  const updateTableData = (
    changedRows: Record<number, SchedulerPackagingRoutesRowType>
  ) => {
    setAllChangedRows({ ...allChangedRows, ...changedRows })
  }

  useEffect(() => {
    if (Object.keys(allChangedRows).length > 0) {
      saveData({ data: allChangedRows, schedulerId, API, iteration })
      setAllChangedRows({})
    }
  }, [allChangedRows])

  const [updateRowsStatus, setUpdateRowsStatus] = useState({})

  const onUploadSuccess = (mergeInfo: Record<string, unknown>) => {
    setIsDataEdited(true)
    setUpdateRowsStatus(mergeInfo)
    toast.success('Data uploaded successfully')
  }
  const onUploadFailure = () => {
    toast.error('Failed to upload data')
  }
  const { mutate: uploadScheduleData, isLoading: isUploading } =
    useUploadScheduleDataFile(onUploadSuccess, onUploadFailure)

  const uploadFile = (file: File) => {
    uploadScheduleData({ file, schedulerId, API, iteration })
  }

  // ─── Side panel handlers (generic, route to any API) ────────────────────────
  const handleSidePanelSave = useCallback(
    (
      api: string,
      changedRows: Record<number, Record<string, unknown>>,
      week?: number
    ) => {
      setIsDataEdited(true)
      saveData({
        data: changedRows as Record<number, SchedulerPackagingRoutesRowType>,
        schedulerId,
        API: api,
        iteration,
        week,
      })
    },
    [saveData, schedulerId, iteration]
  )

  // The factory layout, the product edit panels and the bottom detail know
  // nothing about tanks, so they get the routes with the tank cells blanked
  // (the shape they had before a tank could sit in a Kitchen / Line cell), and
  // what they save gets its tank cells back. See `tankRoutes.ts`.
  const tankSet = useMemo(() => new Set<string>(tankNames), [tankNames])
  const unitRoutes = useMemo(
    () => withoutTanks(data as SchedulerPackagingRoutesRowType[], tankSet),
    [data, tankSet]
  )
  const handleUnitViewSave = useCallback(
    (
      api: string,
      changedRows: Record<number, Record<string, unknown>>,
      week?: number
    ) =>
      handleSidePanelSave(
        api,
        api === 'packing-routes'
          ? restoreTankCells(changedRows, data, tankSet)
          : changedRows,
        week
      ),
    [handleSidePanelSave, data, tankSet]
  )

  const handleSidePanelUpload = useCallback(
    (api: string, file: File, week?: number) => {
      uploadScheduleData({
        file,
        schedulerId,
        API: api,
        iteration,
        weekNumber: week,
      })
    },
    [uploadScheduleData, schedulerId, iteration]
  )

  const handleSidePanelDelete = useCallback(
    async (api: string, rows: Record<string, unknown>[], week?: number) => {
      await deleteData({ rows, schedulerId, API: api, iteration })
    },
    [deleteData, schedulerId, iteration]
  )

  // ─── Contextual tab table handlers ──────────────────────────────────────────
  const demandHandlers = useTableHandlers<ProductionRequirementRowType>(
    schedulerId,
    'production-requirements',
    iteration,
    selectedWeek
  )
  const cleaningHandlers = useTableHandlers<SchedulerCleningMappingRowType>(
    schedulerId,
    'cleaning-mapping',
    iteration
  )
  const syncHandlers = useTableHandlers<SynchronizedLinesRowType>(
    schedulerId,
    'synchronized-lines',
    iteration
  )
  const downtimeHandlers = useTableHandlers<WorkstationDownTimeRowType>(
    schedulerId,
    'workstation-downtime',
    iteration,
    selectedWeek
  )

  // ─── Top tab state ─────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('factory')

  // ─── Resizable factory layout (top = timeline/flow, bottom = output tabs) ──
  const factoryLayoutRef = useRef<HTMLDivElement>(null)
  const [factoryTopHeight, setFactoryTopHeight] = useState<number | null>(null)
  const [topView, setTopView] = useState<'timeline' | 'flow'>('timeline')

  useEffect(() => {
    if (factoryTopHeight === null && factoryLayoutRef.current) {
      setFactoryTopHeight(
        factoryLayoutRef.current.getBoundingClientRect().height * 0.5
      )
    }
  })

  const onFactoryResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const onMouseMove = (ev: MouseEvent) => {
      if (!factoryLayoutRef.current) return
      const rect = factoryLayoutRef.current.getBoundingClientRect()
      const relativeY = ev.clientY - rect.top
      setFactoryTopHeight(Math.max(200, Math.min(rect.height - 200, relativeY)))
    }
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    document.body.style.cursor = 'row-resize'
    document.body.style.userSelect = 'none'
  }, [])

  // ─── Filter state (selection within diagrams) ─────────────────────────────
  const [layoutFilter, setLayoutFilter] = useState<FactoryLayoutFilter>(null)
  const deferredFilter = useDeferredValue(layoutFilter)
  const [detailTab, setDetailTab] = useState(0)
  const [detailMode, setDetailMode] = useState<'input' | 'schedule'>('input')
  // Bottom dock tab: 'schedule' (Gantt) | 'issues' (control panel) — design State 3

  // When selection changes, default to Schedule tab if output exists, otherwise Products
  useEffect(() => {
    setDetailMode('input')
    if (!deferredFilter) {
      setDetailTab(0)
      return
    }
    const detail = precomputedDetails.get(
      `${deferredFilter.type}-${deferredFilter.name}`
    )
    const hasSchedule = (detail?.schedule?.length || 0) > 0

    if (hasSchedule) {
      // Schedule is the last tab: index 2 for kitchen, 4 for line
      if (deferredFilter.type === 'kitchen') setDetailTab(2)
      else if (deferredFilter.type === 'line') setDetailTab(4)
      else setDetailTab(0)
    } else {
      setDetailTab(0)
    }
  }, [deferredFilter?.name, deferredFilter?.type, precomputedDetails])

  // ─── Precompute detail data for ALL nodes upfront ──────────────────────────
  const precomputedDetails = useMemo(() => {
    const lookup = new Map<
      string,
      {
        type: 'kitchen' | 'line' | 'cip'
        products?: SchedulerPackagingRoutesRowType[]
        demand?: ProductionRequirementRowType[]
        cleaning?: SchedulerCleningMappingRowType[]
        syncGroups?: SynchronizedLinesRowType[]
        downtime?: WorkstationDownTimeRowType[]
        lineProductRows?: {
          id: number
          productCode: string
          productName: string
          kitchen: string
          runRate: string
        }[]
        schedule?: InputRow[]
      }
    >()

    // Build product code index for demand lookups
    const demandByProduct = new Map<string, ProductionRequirementRowType[]>()
    for (const pr of productionReqResponse.data) {
      const arr = demandByProduct.get(pr.productCode) || []
      arr.push(pr)
      demandByProduct.set(pr.productCode, arr)
    }

    // Build downtime index by resource
    const downtimeByResource = new Map<string, WorkstationDownTimeRowType[]>()
    for (const dt of downtimeResponse.data) {
      const arr = downtimeByResource.get(dt.resource) || []
      arr.push(dt)
      downtimeByResource.set(dt.resource, arr)
    }

    // Build cleaning index by asset and cip
    const cleaningByAsset = new Map<string, SchedulerCleningMappingRowType[]>()
    const cleaningByCip = new Map<string, SchedulerCleningMappingRowType[]>()
    for (const c of cleaningResponse.data) {
      if (c.asset) {
        const arr = cleaningByAsset.get(c.asset) || []
        arr.push(c)
        cleaningByAsset.set(c.asset, arr)
      }
      if (c.cip) {
        const arr = cleaningByCip.get(c.cip) || []
        arr.push(c)
        cleaningByCip.set(c.cip, arr)
      }
    }

    // Build sync groups index by line
    const syncByLine = new Map<string, SynchronizedLinesRowType[]>()
    for (const sg of syncLinesResponse.data) {
      if (sg.lines) {
        for (const l of sg.lines.split(',').map((s: string) => s.trim())) {
          const arr = syncByLine.get(l) || []
          arr.push(sg)
          syncByLine.set(l, arr)
        }
      }
    }

    // Build route indexes
    const routesByKitchen = new Map<string, SchedulerPackagingRoutesRowType[]>()
    const routesByLine = new Map<string, SchedulerPackagingRoutesRowType[]>()
    // Tank-blind, like the panels it feeds: no TANK01 kitchen or line entry.
    for (const r of unitRoutes) {
      // Kitchen index
      if (r.kitchen) {
        const arr = routesByKitchen.get(r.kitchen) || []
        arr.push(r)
        routesByKitchen.set(r.kitchen, arr)
      }
      // Line index (all 4 slots)
      for (const ln of [
        r.packingLine1,
        r.packingLine2,
        r.packingLine3,
        r.packingLine4,
      ]) {
        if (!ln) continue
        const arr = routesByLine.get(ln) || []
        arr.push(r)
        routesByLine.set(ln, arr)
      }
    }

    // Build schedule index by kitchen and line
    const scheduleByKitchen = new Map<string, InputRow[]>()
    const scheduleByLine = new Map<string, InputRow[]>()
    for (const row of outputRows) {
      if (row.kitchen) {
        const arr = scheduleByKitchen.get(row.kitchen) || []
        arr.push(row)
        scheduleByKitchen.set(row.kitchen, arr)
      }
      if (row.line) {
        const arr = scheduleByLine.get(row.line) || []
        arr.push(row)
        scheduleByLine.set(row.line, arr)
      }
    }

    // Precompute kitchen details
    for (const [kitchenName, products] of routesByKitchen) {
      const productCodes = products.map((r) => r.productCode)
      const demand: ProductionRequirementRowType[] = []
      for (const code of productCodes) {
        const d = demandByProduct.get(code)
        if (d) demand.push(...d)
      }
      lookup.set(`kitchen-${kitchenName}`, {
        type: 'kitchen',
        products,
        demand,
        downtime: downtimeByResource.get(kitchenName) || [],
        schedule: scheduleByKitchen.get(kitchenName) || [],
      })
    }

    // Precompute line details
    for (const [lineName, products] of routesByLine) {
      const productCodes = products.map((r) => r.productCode)
      const demand: ProductionRequirementRowType[] = []
      for (const code of productCodes) {
        const d = demandByProduct.get(code)
        if (d) demand.push(...d)
      }
      const lineProductRows = products.map((r) => {
        let runRate = ''
        if (r.packingLine1 === lineName) runRate = r.lineRunRate1
        else if (r.packingLine2 === lineName) runRate = r.lineRunRate2
        else if (r.packingLine3 === lineName) runRate = r.lineRunRate3
        else if (r.packingLine4 === lineName) runRate = r.lineRunRate4
        return {
          id: r.id,
          productCode: r.productCode,
          productName: r.productName,
          kitchen: r.kitchen,
          runRate,
        }
      })
      lookup.set(`line-${lineName}`, {
        type: 'line',
        products,
        demand,
        cleaning: cleaningByAsset.get(lineName) || [],
        syncGroups: syncByLine.get(lineName) || [],
        downtime: downtimeByResource.get(lineName) || [],
        lineProductRows,
        schedule: scheduleByLine.get(lineName) || [],
      })
    }

    // Precompute CIP details
    const cipNames = new Set(
      cleaningResponse.data
        .map((c: SchedulerCleningMappingRowType) => c.cip)
        .filter(Boolean)
    )
    for (const cipName of cipNames) {
      lookup.set(`cip-${cipName}`, {
        type: 'cip',
        cleaning: cleaningByCip.get(cipName) || [],
        downtime: downtimeByResource.get(cipName) || [],
      })
    }

    return lookup
  }, [
    unitRoutes,
    productionReqResponse.data,
    cleaningResponse.data,
    syncLinesResponse.data,
    downtimeResponse.data,
    outputRows,
  ])

  // ─── Instant lookup on selection change (uses deferred filter so diagram stays fast) ──
  const detailData = deferredFilter
    ? precomputedDetails.get(`${deferredFilter.type}-${deferredFilter.name}`) ||
      null
    : null
  const filteredData = detailData?.products || data
  const lineProductRows = detailData?.lineProductRows || []

  const detailTabs = useMemo(() => {
    if (!deferredFilter || !detailData) return []
    if (deferredFilter.type === 'kitchen') {
      return [
        { label: 'Products', count: detailData.products?.length || 0 },
        { label: 'Downtime', count: detailData.downtime?.length || 0 },
        ...(detailData.schedule?.length
          ? [{ label: 'Schedule', count: detailData.schedule.length }]
          : []),
      ]
    }
    if (deferredFilter.type === 'line') {
      return [
        { label: 'Products', count: detailData.products?.length || 0 },
        { label: 'Cleaning Programs', count: detailData.cleaning?.length || 0 },
        // Sync Groups removed alongside the schedule view's own Sync Groups
        // tab. `activeDetail` below maps tabs to content by INDEX, so the
        // switch's cases were renumbered in the same change — dropping an
        // entry here on its own would silently shift every later tab onto the
        // wrong content.
        { label: 'Downtime', count: detailData.downtime?.length || 0 },
        ...(detailData.schedule?.length
          ? [{ label: 'Schedule', count: detailData.schedule.length }]
          : []),
      ]
    }
    if (deferredFilter.type === 'cip') {
      return [
        { label: 'Cleaning Programs', count: detailData.cleaning?.length || 0 },
        { label: 'Downtime', count: detailData.downtime?.length || 0 },
      ]
    }
    return []
  }, [deferredFilter, detailData])

  const lineProductCols: GridColDef[] = useMemo(
    () => [
      { field: 'productCode', headerName: 'Product Code', flex: 1 },
      { field: 'productName', headerName: 'Product Name', flex: 1.5 },
      { field: 'kitchen', headerName: 'Kitchen', flex: 0.8 },
      { field: 'runRate', headerName: 'Run Rate', flex: 0.6, type: 'number' },
    ],
    []
  )

  // Products columns for kitchen context
  const kitchenProductCols: GridColDef[] = useMemo(
    () => [
      { field: 'productCode', headerName: 'Product Code', flex: 1 },
      { field: 'productName', headerName: 'Product Name', flex: 1.5 },
      {
        field: 'kitchenRunRate',
        headerName: 'Kitchen Run Rate',
        flex: 0.8,
        type: 'number',
      },
    ],
    []
  )

  // Production Requirements — simplified
  const demandCols: GridColDef[] = useMemo(
    () => [
      { field: 'productCode', headerName: 'Product Code', flex: 1 },
      { field: 'quantity', headerName: 'Quantity', flex: 0.7, type: 'number' },
      {
        field: 'dontStartBefore',
        headerName: 'Start After (hr)',
        flex: 0.7,
        type: 'number',
      },
      {
        field: 'finishBefore',
        headerName: 'Finish By (hr)',
        flex: 0.7,
        type: 'number',
      },
    ],
    []
  )

  // Cleaning Programs — simplified
  const cleaningCols: GridColDef[] = useMemo(
    () => [
      { field: 'programName', headerName: 'Program', flex: 1 },
      { field: 'cip', headerName: 'CIP', flex: 0.7 },
      {
        field: 'totalTargetTime',
        headerName: 'Duration',
        flex: 0.6,
        type: 'number',
      },
    ],
    []
  )

  // Sync Groups — already simple
  const syncCols: GridColDef[] = useMemo(
    () => [
      { field: 'groupName', headerName: 'Group', flex: 0.8 },
      { field: 'lines', headerName: 'Lines', flex: 1.5 },
      {
        field: 'changeoverTime',
        headerName: 'Changeover (hrs)',
        flex: 0.7,
        type: 'number',
      },
    ],
    []
  )

  // Downtime — simplified
  const downtimeCols: GridColDef[] = useMemo(
    () => [
      { field: 'resource', headerName: 'Resource', flex: 1 },
      { field: 'downTime', headerName: 'Down Time', flex: 1.5 },
    ],
    []
  )

  // ─── Resolve active detail tab into rows + columns (no JSX, just data) ────
  const activeDetail = useMemo<
    | { rows: Record<string, unknown>[]; columns: GridColDef[] }
    | 'gantt'
    | 'schedule'
    | 'products-edit'
    | 'kitchen-products-edit'
    | null
  >(() => {
    if (!deferredFilter || !detailData) return null

    if (deferredFilter.type === 'kitchen') {
      switch (detailTab) {
        case 0:
          return 'kitchen-products-edit'
        case 1:
          return 'gantt'
        case 2:
          return detailData.schedule?.length ? 'schedule' : null
        default:
          return null
      }
    }
    if (deferredFilter.type === 'line') {
      // Indices must match `detailTabs`'s line list exactly:
      // 0 Products, 1 Cleaning Programs, 2 Downtime, 3 Schedule.
      switch (detailTab) {
        case 0:
          return 'products-edit'
        case 1:
          return {
            rows: (detailData.cleaning || []) as Record<string, unknown>[],
            columns: cleaningCols,
          }
        case 2:
          return 'gantt'
        case 3:
          return detailData.schedule?.length ? 'schedule' : null
        default:
          return null
      }
    }
    if (deferredFilter.type === 'cip') {
      switch (detailTab) {
        case 0:
          return {
            rows: (detailData.cleaning || []) as Record<string, unknown>[],
            columns: cleaningCols,
          }
        case 1:
          return 'gantt'
        default:
          return null
      }
    }
    return null
  }, [
    deferredFilter,
    detailData,
    detailTab,
    kitchenProductCols,
    demandCols,
    lineProductRows,
    lineProductCols,
    cleaningCols,
    syncCols,
  ])

  // Detail content for when a kitchen/line is selected — FactoryBottomDetail equivalent.
  // Header matches the Scheduler design language: node name + type label + clear × button.
  const detailContent =
    deferredFilter && detailData ? (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          opacity: deferredFilter !== layoutFilter ? 0.6 : 1,
          transition: 'opacity 0.15s',
        }}
      >
        {/* Header bar — design: node name, type badge, Input/Schedule toggle, × close */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: '14px',
            py: '8px',
            borderBottom: '1px solid #DBE2E7',
            background: '#F3F6F8',
            flexShrink: 0,
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: 9.5,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: '#71808A',
              fontFamily: "'Archivo', system-ui, sans-serif",
            }}
          >
            {deferredFilter.type === 'kitchen'
              ? 'Kitchen'
              : deferredFilter.type === 'line'
                ? 'Line'
                : 'CIP'}
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: '#0E1B22',
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            }}
          >
            {deferredFilter.name}
          </Box>
          <Box sx={{ flex: 1 }} />
          {deferredFilter.type !== 'cip' &&
            (detailData.schedule?.length || 0) > 0 && (
              <Box sx={{ display: 'flex', gap: '2px' }}>
                {['input', 'schedule'].map((m) => {
                  const on = detailMode === m
                  return (
                    <Box
                      key={m}
                      component="button"
                      onClick={() => setDetailMode(m as 'input' | 'schedule')}
                      sx={{
                        border: `1px solid ${on ? '#00415A' : '#C5D0D7'}`,
                        background: on ? '#00415A' : '#fff',
                        color: on ? '#fff' : '#51616C',
                        borderRadius: '2px',
                        px: '10px',
                        py: '3px',
                        fontSize: 11.5,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: "'Archivo', system-ui, sans-serif",
                        textTransform: 'capitalize',
                      }}
                    >
                      {m}
                    </Box>
                  )
                })}
              </Box>
            )}
          <Box
            component="button"
            onClick={() => setLayoutFilter(null)}
            title="Clear selection"
            sx={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              p: '4px',
              borderRadius: '2px',
              color: '#71808A',
              ml: 0.5,
              '&:hover': { background: 'rgba(0,65,90,0.06)' },
            }}
          >
            <ClearIcon sx={{ fontSize: 16 }} />
          </Box>
        </Box>

        {/* Content area — scrollable, fills remaining dock height */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
          {detailMode === 'schedule' &&
          deferredFilter.type !== 'cip' &&
          (detailData.schedule?.length || 0) > 0 ? (
            <Box sx={{ mt: 1 }}>
              <ResourceGantt
                rows={detailData.schedule || []}
                mode={deferredFilter.type as 'kitchen' | 'line'}
                resourceName={deferredFilter.name}
              />
            </Box>
          ) : (
            <>
              <Tabs
                value={detailTab >= 0 ? detailTab : false}
                onChange={(_, v) => setDetailTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  minHeight: 36,
                  '& .MuiTab-root': {
                    minHeight: 36,
                    py: 0.5,
                    textTransform: 'none',
                    fontSize: '0.85rem',
                  },
                }}
              >
                {detailTabs.map((tab) => (
                  <Tab
                    key={tab.label}
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        {tab.label}
                        <Badge
                          badgeContent={tab.count}
                          color={tab.count > 0 ? 'primary' : 'default'}
                          max={999}
                          sx={{
                            '& .MuiBadge-badge': {
                              fontSize: '0.65rem',
                              height: 16,
                              minWidth: 16,
                              position: 'relative',
                              transform: 'none',
                            },
                          }}
                        />
                      </Box>
                    }
                  />
                ))}
              </Tabs>
              {detailTab >= 0 && (
                <Box sx={{ mt: 1 }}>
                  {activeDetail === 'kitchen-products-edit' ? (
                    <KitchenProductEditPanel
                      kitchenName={deferredFilter!.name}
                      packagingRoutes={unitRoutes}
                      onSaveRoutes={handleUnitViewSave}
                    />
                  ) : activeDetail === 'products-edit' ? (
                    <ProductEditPanel
                      lineName={deferredFilter!.name}
                      packagingRoutes={unitRoutes}
                      productionRequirements={productionReqResponse.data || []}
                      selectedWeek={selectedWeek}
                      onWeekChange={setSelectedWeek}
                      onSaveRoutes={handleUnitViewSave}
                      onSaveRequirements={(rows) =>
                        demandHandlers.updateTableData(rows)
                      }
                    />
                  ) : activeDetail === 'gantt' ? (
                    <DowntimeGantt
                      downtimeRows={downtimeResponse.data}
                      resourceName={deferredFilter!.name}
                      onSave={downtimeHandlers.updateTableData}
                    />
                  ) : activeDetail === 'schedule' ? (
                    <ResourceGantt
                      rows={detailData!.schedule || []}
                      mode={deferredFilter!.type as 'kitchen' | 'line'}
                      resourceName={deferredFilter!.name}
                    />
                  ) : activeDetail ? (
                    <DetailTable
                      rows={activeDetail.rows}
                      columns={activeDetail.columns}
                    />
                  ) : null}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    ) : undefined

  return (
    <Box>
      {!forceViewMode && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
            mb: 1,
          }}
        >
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, val) => {
              if (val) {
                setViewMode(val)
                if (val === 'table') setLayoutFilter(null)
              }
            }}
            size="small"
          >
            <ToggleButton value="layout">
              <Tooltip title="Factory Layout + Table">
                <AccountTreeIcon />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table">
              <Tooltip title="Table Only">
                <TableChartIcon />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {viewMode === 'layout' && (
        <>
          {/* ── Resizable split: Timeline/Flow (top) + Output tabs (bottom) ── */}
          <Box
            ref={factoryLayoutRef}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: 'calc(100vh - 200px)',
              minHeight: 600,
            }}
          >
            {/* ── Top: Timeline or Factory-map (resizable) ── */}
            <Box
              sx={{
                height: factoryTopHeight ?? '50%',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              {topView === 'timeline' ? (
                <TimelineTab
                  outputRows={outputRows}
                  weekBaseHour={midWeekBaseHour}
                  selectedWeek={selectedWeek}
                  productionRequirements={productionReqResponse.data ?? []}
                  syncGroups={syncLinesResponse.data ?? []}
                  downtimeData={downtimeResponse.data ?? []}
                  midWeekRunning={isMidWeekRunning}
                  midWeekProgress={midWeekProgress}
                  scheduleId={schedulerId}
                  iteration={iteration}
                  outputVariant={outputVariant}
                  onOutputVariantChange={setOutputVariant}
                  hasReschedule={hasReschedule}
                  selectedRun={selectedRun}
                  onSelectRun={setSelectedRun}
                  onExplainRun={explainRun}
                  headerRight={
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '2px',
                        ml: '8px',
                        pl: '12px',
                        borderLeft: '1px solid #DBE2E7',
                      }}
                    >
                      {(
                        [
                          ['timeline', 'Timeline'],
                          ['flow', 'Factory map'],
                        ] as const
                      ).map(([v, label]) => (
                        <Box
                          key={v}
                          component="button"
                          onClick={() => setTopView(v)}
                          sx={{
                            border: `1px solid ${topView === v ? '#0E7C86' : '#C5D0D7'}`,
                            borderRadius: '2px',
                            px: '8px',
                            height: 24,
                            cursor: 'pointer',
                            bgcolor:
                              topView === v ? 'rgba(14,124,134,0.08)' : '#fff',
                            fontSize: 10.5,
                            fontWeight: 600,
                            color: topView === v ? '#0E7C86' : '#51616C',
                            fontFamily: "'Archivo', system-ui, sans-serif",
                          }}
                        >
                          {label}
                        </Box>
                      ))}
                    </Box>
                  }
                />
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    position: 'relative',
                    overflow: 'auto',
                    bgcolor: 'var(--surface-50, #F3F6F8)',
                    backgroundImage:
                      'radial-gradient(#DBE2E7 1px, transparent 1px)',
                    backgroundSize: '22px 22px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* View toggle: overlaid top-right */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 12,
                      zIndex: 20,
                      display: 'flex',
                      gap: '2px',
                    }}
                  >
                    {(
                      [
                        ['timeline', 'Timeline'],
                        ['flow', 'Factory map'],
                      ] as const
                    ).map(([v, label]) => (
                      <Box
                        key={v}
                        component="button"
                        onClick={() => setTopView(v)}
                        sx={{
                          border: `1px solid ${topView === v ? '#0E7C86' : '#C5D0D7'}`,
                          borderRadius: '2px',
                          px: '8px',
                          height: 24,
                          cursor: 'pointer',
                          bgcolor:
                            topView === v ? 'rgba(14,124,134,0.08)' : '#fff',
                          fontSize: 10.5,
                          fontWeight: 600,
                          color: topView === v ? '#0E7C86' : '#51616C',
                          fontFamily: "'Archivo', system-ui, sans-serif",
                          boxShadow: '0 1px 4px rgba(14,27,34,0.14)',
                        }}
                      >
                        {label}
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <FactoryLayout
                      schedulerId={schedulerId}
                      iteration={iteration}
                      packagingRoutes={unitRoutes}
                      downtimeData={downtimeResponse.data}
                      cleaningMapping={cleaningResponse.data}
                      productionRequirements={productionReqResponse.data}
                      synchronizedLines={syncLinesResponse.data}
                      outputData={outputRows}
                      consolidatedWeeks={consolidatedWeeks}
                      dataSummary={dataSummary}
                      onFilterChange={setLayoutFilter}
                      onSaveData={handleUnitViewSave}
                      onUploadFile={handleSidePanelUpload}
                      onDeleteRows={handleSidePanelDelete}
                      selectedWeek={selectedWeek}
                      onWeekChange={setSelectedWeek}
                      pendingComment={pendingComment}
                      onClearPendingComment={() => setPendingComment(null)}
                      notProducedIds={notProducedIds}
                      onResetNotProduced={() => setNotProducedIds(new Set())}
                      hideSidePanel
                    />
                  </Box>
                </Box>
              )}
            </Box>

            {/* ── Drag handle ── */}
            <Box
              onMouseDown={onFactoryResizeMouseDown}
              sx={{
                height: 10,
                flexShrink: 0,
                cursor: 'row-resize',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.100',
                '&:hover': { bgcolor: 'grey.300' },
                transition: 'background-color 0.15s',
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 3,
                  borderRadius: 1.5,
                  bgcolor: 'grey.400',
                }}
              />
            </Box>

            {/* ── Bottom: output tabs (By product, Crew, Resources, Demand, Sync) ── */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {layoutFilter ? (
                <FactoryBottomDetail
                  name={layoutFilter.name}
                  type={layoutFilter.type}
                  onClear={() => setLayoutFilter(null)}
                  packagingRoutes={unitRoutes}
                  downtimeData={downtimeResponse.data}
                  cleaningData={cleaningResponse.data}
                  syncData={syncLinesResponse.data}
                  scheduleRows={outputRows}
                  hasRun={outputRows.length > 0}
                  selectedWeek={selectedWeek}
                  onOpenTable={(tbl) => {
                    const TABLE_MAP: Record<string, string> = {
                      'Production Requirement': 'tab-0',
                      'Product Master Data': 'tab-7',
                      'Packaging Routes': 'tab-5',
                      'Cleaning Mapping': 'tab-2',
                      'Workstation Downtime': 'tab-1',
                      'Synchronized Lines': 'tab-10',
                    }
                    void TABLE_MAP[tbl]
                  }}
                />
              ) : (
                <FactoryScheduleView
                  scheduleId={schedulerId}
                  iteration={iteration}
                  outputRows={outputRows}
                  selectedWeek={selectedWeek}
                  productionRequirements={productionReqResponse.data}
                  syncGroups={syncLinesResponse.data}
                  constraints={constraintsResponse.data}
                  packagingRoutes={unitRoutes}
                  downtimeRows={downtimeResponse.data}
                  notProducedIds={notProducedIds}
                  onToggleNotProduced={onToggleNotProduced}
                  onSaveDowntimeAsync={downtimeHandlers.updateTableDataAsync}
                  requirementRows={productionReqResponse.data}
                  onSaveRequirementsAsync={demandHandlers.updateTableDataAsync}
                  stillToMake={midWeekStillToMake}
                  setStillToMake={setMidWeekStillToMake}
                  dontStartBefore={midWeekDontStartBefore}
                  setDontStartBefore={setMidWeekDontStartBefore}
                  finishBefore={midWeekFinishBefore}
                  setFinishBefore={setMidWeekFinishBefore}
                  weekBaseHour={midWeekBaseHour}
                  midWeekOutputRows={midWeekOutputRows}
                  runMidWeekReschedule={runMidWeekReschedule}
                  isMidWeekRunning={isMidWeekRunning}
                  modelFocus={selectedRun}
                  onClearModelFocus={() => setSelectedRun(null)}
                  revealModelTick={revealModelTick}
                />
              )}
            </Box>
          </Box>

          {activeTab !== 'factory' && (
            <Box
              sx={{
                display: 'flex',
                height: 600,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {activeTab === 'changeovers' && (
                  <ScheduleChangeoverView
                    schedulerId={schedulerId}
                    iteration={iteration}
                  />
                )}
                {activeTab === 'constraints' && (
                  <ScheduleConstraintCardsView
                    schedulerId={schedulerId}
                    iteration={iteration}
                  />
                )}
                {activeTab === 'crew' && (
                  <ScheduleCrewSyncView
                    schedulerId={schedulerId}
                    iteration={iteration}
                  />
                )}
              </Box>
              <ScheduleControlPanel
                schedulerId={schedulerId}
                iteration={iteration}
                selectedWeek={selectedWeek}
                onWeekChange={setSelectedWeek}
                outputRows={outputRows}
                dataSummary={dataSummary}
                notProducedIds={notProducedIds}
                onResetNotProduced={() => setNotProducedIds(new Set())}
                onRunComplete={() => {
                  // Bring the Gantt back into view — this panel only shows
                  // while looking at changeovers/constraints/crew, not the
                  // schedule itself.
                  setActiveTab('factory')
                  onGoToScheduleTab?.()
                }}
              />
            </Box>
          )}

          {/* Bottom schedule for non-factory tabs */}

          {/* Detail section when a node is selected (shown outside factory tab only) */}
          {activeTab !== 'factory' && deferredFilter && detailData && (
            <Box
              sx={{
                mt: 2,
                opacity: deferredFilter !== layoutFilter ? 0.6 : 1,
                transition: 'opacity 0.15s',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FilterListIcon
                    sx={{ fontSize: 18, color: 'text.secondary' }}
                  />
                  <Chip
                    label={`${deferredFilter.type === 'kitchen' ? 'Kitchen' : deferredFilter.type === 'line' ? 'Line' : 'CIP'}: ${deferredFilter.name}`}
                    size="small"
                    color="primary"
                    onDelete={() => setLayoutFilter(null)}
                    deleteIcon={<ClearIcon />}
                  />
                </Box>
                {deferredFilter.type !== 'cip' &&
                  (detailData.schedule?.length || 0) > 0 && (
                    <ToggleButtonGroup
                      value={detailMode}
                      exclusive
                      onChange={(_, val) => {
                        if (val) setDetailMode(val)
                      }}
                      size="small"
                      sx={{
                        '& .MuiToggleButton-root': {
                          py: 0.25,
                          px: 1.5,
                          fontSize: '0.75rem',
                          textTransform: 'none',
                        },
                      }}
                    >
                      <ToggleButton value="input">Input</ToggleButton>
                      <ToggleButton value="schedule">Schedule</ToggleButton>
                    </ToggleButtonGroup>
                  )}
              </Box>

              {detailMode === 'schedule' &&
              deferredFilter.type !== 'cip' &&
              (detailData.schedule?.length || 0) > 0 ? (
                <Box sx={{ mt: 1 }}>
                  <ResourceGantt
                    rows={detailData.schedule || []}
                    mode={deferredFilter.type as 'kitchen' | 'line'}
                    resourceName={deferredFilter.name}
                  />
                </Box>
              ) : (
                <>
                  <Tabs
                    value={detailTab >= 0 ? detailTab : false}
                    onChange={(_, v) => setDetailTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      minHeight: 36,
                      '& .MuiTab-root': {
                        minHeight: 36,
                        py: 0.5,
                        textTransform: 'none',
                        fontSize: '0.85rem',
                      },
                    }}
                  >
                    {detailTabs.map((tab) => (
                      <Tab
                        key={tab.label}
                        label={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                            }}
                          >
                            {tab.label}
                            <Badge
                              badgeContent={tab.count}
                              color={tab.count > 0 ? 'primary' : 'default'}
                              max={999}
                              sx={{
                                '& .MuiBadge-badge': {
                                  fontSize: '0.65rem',
                                  height: 16,
                                  minWidth: 16,
                                  position: 'relative',
                                  transform: 'none',
                                },
                              }}
                            />
                          </Box>
                        }
                      />
                    ))}
                  </Tabs>
                  {detailTab >= 0 && (
                    <Box sx={{ mt: 1 }}>
                      {activeDetail === 'kitchen-products-edit' ? (
                        <KitchenProductEditPanel
                          kitchenName={deferredFilter!.name}
                          packagingRoutes={unitRoutes}
                          onSaveRoutes={handleUnitViewSave}
                        />
                      ) : activeDetail === 'products-edit' ? (
                        <ProductEditPanel
                          lineName={deferredFilter!.name}
                          packagingRoutes={unitRoutes}
                          productionRequirements={
                            productionReqResponse.data || []
                          }
                          selectedWeek={selectedWeek}
                          onWeekChange={setSelectedWeek}
                          onSaveRoutes={handleUnitViewSave}
                        />
                      ) : activeDetail === 'gantt' ? (
                        <DowntimeGantt
                          downtimeRows={downtimeResponse.data}
                          resourceName={deferredFilter!.name}
                          onSave={downtimeHandlers.updateTableData}
                        />
                      ) : activeDetail === 'schedule' ? (
                        <ResourceGantt
                          rows={detailData!.schedule || []}
                          mode={deferredFilter!.type as 'kitchen' | 'line'}
                          resourceName={deferredFilter!.name}
                        />
                      ) : activeDetail ? (
                        <DetailTable
                          rows={activeDetail.rows}
                          columns={activeDetail.columns}
                        />
                      ) : null}
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </>
      )}

      {viewMode === 'table' && (
        <Box sx={{ height: 'calc(100vh - 240px)', minHeight: 400 }}>
          <SchedulerPackagingRoutesTable
            data={data}
            isLoading={isLoading}
            updateTableData={updateTableData}
            uploadScheduleData={uploadFile}
            isUploading={isUploading}
            isSaving={isUpdating}
            updateRowsStatus={updateRowsStatus}
            predicate={predicate}
            deleteTableData={deleteTableData}
            isDeleting={isDeleting}
            showTankColumns={showTankColumns}
            tankNames={tankNames}
            showLegacyTankColumns={hasLegacyTankColumns}
          />
        </Box>
      )}
    </Box>
  )
}
